/**
 * Login screen (PRD §3, ARCHITECTURE §5.1 #1, §10.2).
 *
 * Single password prompt: `401` renders the server's message inline, `429` renders the lockout
 * with its `Retry-After`, success invalidates the auth query and returns the user to wherever the
 * gate intercepted them.
 */

import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { login } from '../../api/auth';
import { QK } from '../../api/queryKeys';
import { isApiError } from '../../api/client';
import { Spinner } from '../../components';
import { APP_NAME, pageTitle } from '../../app/appTitle';
import { HOME_PATH } from '../../app/routes';
import { useAuth } from '../../app/useAuth';
import { useDocumentTitle } from '../../lib/hooks';
import './login.css';

function formatRetryAfter(seconds: number | null): string {
  if (seconds === null || seconds <= 0) return 'Please try again later.';
  if (seconds < 60) return `Try again in ${seconds} seconds.`;
  const minutes = Math.ceil(seconds / 60);
  return `Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`;
}

function loginErrorMessage(error: unknown): string {
  if (!isApiError(error)) return 'Login failed. Please try again.';
  // The server owns the wording whenever it sent an error envelope; these fallbacks only cover a
  // response that carried no body at all.
  const sent = error.code === 'unexpected_response' ? '' : error.message;
  if (error.status === 429) {
    return `${sent || 'Too many failed attempts.'} ${formatRetryAfter(error.retryAfterSeconds)}`;
  }
  if (error.status === 401) return sent || 'Incorrect password.';
  return error.message;
}

type RedirectState = { from?: unknown } | null;

function redirectTarget(state: RedirectState): string {
  const from = state?.from;
  return typeof from === 'string' && from.startsWith('/') && !from.startsWith('//')
    ? from
    : HOME_PATH;
}

export function LoginPage() {
  const [password, setPassword] = useState('');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { authenticated } = useAuth();
  const target = redirectTarget(location.state as RedirectState);
  // Above the `authenticated` early return — hooks may not be conditional.
  useDocumentTitle(pageTitle('Sign in'));

  const mutation = useMutation({
    mutationFn: (submitted: string) => login({ password: submitted }),
    onSuccess: async () => {
      setPassword('');
      await queryClient.invalidateQueries({ queryKey: QK.auth });
      void navigate(target, { replace: true });
    },
  });

  if (authenticated) return <Navigate to={target} replace />;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password.length === 0 || mutation.isPending) return;
    mutation.mutate(password);
  };

  return (
    <div className="center-fill">
      <div className="card login-card">
        <div className="login-card__brand">
          <h1 className="login-card__title">{APP_NAME}</h1>
          <p className="muted">Enter the app password to continue.</p>
        </div>

        <form className="login-card__form" onSubmit={onSubmit}>
          <div className="field">
            <label className="field__label" htmlFor="login-password">
              Password
            </label>
            <input
              id="login-password"
              className="input"
              type="password"
              name="password"
              autoComplete="current-password"
              autoFocus
              required
              value={password}
              disabled={mutation.isPending}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>

          {mutation.isError ? (
            <p className="form-error" role="alert">
              {loginErrorMessage(mutation.error)}
            </p>
          ) : null}

          <button
            type="submit"
            className="btn-primary login-card__submit"
            disabled={mutation.isPending || password.length === 0}
          >
            {mutation.isPending ? <Spinner size="sm" /> : null}
            {mutation.isPending ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default LoginPage;
